import React, { useState } from 'react';
import { createVehicle } from '../api/vehicles';
import { useAuth } from '../context/AuthContext';
import VinInput, { isVinFormatValid } from '../components/VinInput';
import CompanySelect, { TECH_CHECK_COMPANIES } from '../components/CompanySelect';
import { trackPixelCustom } from '../utils/metaPixel';

const inputClass = "w-full bg-[#100F0D] border border-[#2B2724] text-[#F4F1EA] placeholder-[#5C554C] px-3 py-2.5 rounded-[9px] text-[13px] focus:border-[#4E4740] outline-none transition-colors";
const labelClass = "text-[11px] text-[#8F877B] mb-1.5 block";

const AddVehicle = ({ token }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    make: '',
    model: '',
    year: '',
    plateNumber: '',
    vin: '',
    odometerReading: '',
    nextOilChangeOdometer: '',
    insuranceCompany: '',
    insuranceExpiry: '',
    technicalCheckCompany: '',
    technicalExpiry: '',
    distanceUnit: 'km',
    fuelType: 'Essence'
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (
      !form.insuranceExpiry ||
      !form.technicalExpiry ||
      !form.insuranceCompany ||
      !form.technicalCheckCompany
    ) {
      setError('Tous les champs assurance et controle technique sont requis.');
      return;
    }

    if (!isVinFormatValid(form.vin)) {
      setError('Le VIN doit contenir exactement 17 caracteres.');
      return;
    }

    const loaderId = user?._id || user?.id;
    const odometerReading = Number(form.odometerReading);
    const nextOilChangeOdometer = Number(form.nextOilChangeOdometer);
    const year = form.year ? String(form.year) : '';
    const insuranceExpiry = form.insuranceExpiry ? new Date(form.insuranceExpiry).toISOString() : '';
    const technicalExpiry = form.technicalExpiry ? new Date(form.technicalExpiry).toISOString() : '';

    const payload = {
      brand: form.make,
      model: form.model,
      year,
      plateNumber: form.plateNumber,
      vin: form.vin,
      odometerReading,
      nextOilChangeOdometer,
      distanceUnit: form.distanceUnit || 'km',
      fuelType: form.fuelType,
      loader: loaderId,
      serviceWorkshops: [loaderId],
      insurance: {
        company: form.insuranceCompany,
        expiry: insuranceExpiry
      },
      technicalCheck: {
        company: form.technicalCheckCompany,
        expiry: technicalExpiry
      }
    };

    try {
      await createVehicle(payload, token);
      trackPixelCustom('VehicleAdded', { source: 'owner' });
      setMessage('Vehicule enregistre avec succes');
      setForm({
        make: '', model: '', year: '', plateNumber: '', vin: '',
        odometerReading: '', nextOilChangeOdometer: '',
        insuranceCompany: '', insuranceExpiry: '',
        technicalCheckCompany: '', technicalExpiry: '',
        distanceUnit: 'km', fuelType: 'Essence'
      });
    } catch (err) {
      console.error("Submit error:", err?.response?.data || err.message);
      setError(err?.response?.data?.error || 'Echec de l\'enregistrement');
    }
  };

  return (
    <div className="text-[#F4F1EA] font-['IBM_Plex_Sans',system-ui,sans-serif] antialiased">
      <main className="w-full max-w-[900px] px-[30px] pt-[26px] pb-[60px]">
      <header className="mb-[18px]">
        <p className="text-[11px] uppercase tracking-[.2em] text-[#6E665C] mb-1.5">Flotte</p>
        <h1 className="font-['Space_Grotesk',sans-serif] text-[30px] font-semibold tracking-[-.025em] leading-[1.1]">
          Ajouter un véhicule
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vehicle Info */}
        <div className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] p-[18px] space-y-4">
          <h3 className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-2">Informations vehicule</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Marque</label>
              <input name="make" placeholder="Ex: Toyota" value={form.make} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Modele</label>
              <input name="model" placeholder="Ex: Corolla" value={form.model} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Annee</label>
              <input name="year" placeholder="Ex: 2020" value={form.year} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Immatriculation</label>
              <input name="plateNumber" placeholder="Ex: DK-1234-AA" value={form.plateNumber} onChange={handleChange} required className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass}>VIN (17 caracteres)</label>
            <VinInput value={form.vin} onChange={handleChange} required className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Type de carburant</label>
              <select name="fuelType" value={form.fuelType} onChange={handleChange} required className={inputClass}>
                <option value="Essence">Essence</option>
                <option value="Gasoil">Gasoil (Diesel)</option>
                <option value="Hybrid">Hybride</option>
                <option value="Electric">Electrique</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Unite de distance</label>
              <select name="distanceUnit" value={form.distanceUnit} onChange={handleChange} required className={inputClass}>
                <option value="km">Kilometre (km)</option>
                <option value="mi">Mile (mi)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Odometer */}
        <div className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] p-[18px] space-y-4">
          <h3 className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-2">Kilometrage</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Compteur actuel ({form.distanceUnit})</label>
              <input name="odometerReading" type="number" placeholder="Ex: 45000" value={form.odometerReading} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Prochaine vidange ({form.distanceUnit})</label>
              <input name="nextOilChangeOdometer" type="number" placeholder="Ex: 50000" value={form.nextOilChangeOdometer} onChange={handleChange} required className={inputClass} />
            </div>
          </div>
        </div>

        {/* Insurance & Technical */}
        <div className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] p-[18px] space-y-4">
          <h3 className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-2">Assurance & Controle technique</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Compagnie d'assurance</label>
              {/* Free text for now — switch back to a dropdown once the full list
                  of market insurers is gathered. */}
              <input
                name="insuranceCompany"
                type="text"
                value={form.insuranceCompany}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="Nom de la compagnie d'assurance"
              />
            </div>
            <div>
              <label className={labelClass}>Date d'expiration assurance</label>
              <input name="insuranceExpiry" type="date" value={form.insuranceExpiry} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Centre de controle technique</label>
              <CompanySelect
                value={form.technicalCheckCompany}
                onChange={(v) => setForm(prev => ({ ...prev, technicalCheckCompany: v }))}
                options={TECH_CHECK_COMPANIES}
                className={inputClass}
                placeholder="Sélectionner un centre"
              />
            </div>
            <div>
              <label className={labelClass}>Date d'expiration controle</label>
              <input name="technicalExpiry" type="date" value={form.technicalExpiry} onChange={handleChange} required className={inputClass} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full h-[40px] rounded-[9px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] hover:from-white hover:to-[#DED9CE] text-[#141210] text-[13px] font-semibold transition-colors"
        >
          Enregistrer le véhicule
        </button>
      </form>

      {message && <p className="mt-4 text-center text-[12.5px] rounded-xl px-4 py-3" style={{background:'rgba(116,169,141,.13)',color:'#74A98D'}}>{message}</p>}
      {error && <p className="mt-4 text-center text-[12.5px] rounded-xl px-4 py-3" style={{background:'rgba(226,87,75,.13)',color:'#E2574B'}}>{error}</p>}
      </main>
    </div>
  );
};

export default AddVehicle;
